import React, { useEffect, useState } from "react";
import axios from "axios";
import CONFIG from "../../config";
import { usePatient } from "../../context/PatientContext";
import "./PatientPaymentReport.css";

const baseURL = CONFIG.BASE_URL;

const formatCurrency = (amt) => {
  if (amt === null || amt === undefined) return "₹0.00";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(amt);
};

const formatDate = (dt) => {
  if (!dt) return "-";
  const d = new Date(dt);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
};

export default function PatientPaymentReport() {
  const { doctorInfo, token } = usePatient();

  const [patientId, setPatientId] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0, netBalance: 0 });

  useEffect(() => {
    // if patientId is present by default, we can fetch automatically. Not doing auto fetch to avoid unexpected calls.
  }, []);

  const buildHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken") || token || ""}`,
    doctorId: doctorInfo?.MANDALI_ID || "",
    clinicId: doctorInfo?.CLINIC_ID || "",
    roleId: doctorInfo?.MANDALI_ROLE || "",
  });

  const computeTotals = (rows) => {
    let totalDebit = 0;
    let totalCredit = 0;
    let netBalance = 0;

    rows.forEach((r) => {
      const charges = (Number(r.CONSULTING_CHARGES || 0) + Number(r.PANCHKARMA_CHARGES || 0) + Number(r.MEDICINE_CHARGES || 0));
      totalDebit += charges;
      totalCredit += Number(r.CURRENT_PAYMENT || 0);
      netBalance = Number(r.NEW_BALANCE || netBalance);
    });

    setTotals({ totalDebit, totalCredit, netBalance });
  };

  const fetchPayments = async () => {
    setError(null);

    if (!patientId) {
      setError("Please enter a patient ID to fetch the report.");
      return;
    }

    if (!startDate || !endDate) {
      setError("Please provide both start and end dates.");
      return;
    }

    setLoading(true);
    try {
      // Ensure endDate includes end of day
      const s = `${startDate} 00:00:00`;
      const e = `${endDate} 23:59:59`;

      const url = `${baseURL}/payments/patient/${encodeURIComponent(patientId)}/daterange?startDate=${encodeURIComponent(s)}&endDate=${encodeURIComponent(e)}`;

      const resp = await axios.get(url, { headers: buildHeaders() });

      const data = resp.data?.data || resp.data || [];

      // If API returned a message for no data, it may be in { message, data: [] }
      const rows = Array.isArray(data) ? data : (Array.isArray(resp.data) ? resp.data : []);

      setPayments(rows);
      computeTotals(rows);
    } catch (err) {
      console.error("Error fetching payments:", err);
      setError(err.response?.data?.error || err.message || "Failed to fetch payments");
      setPayments([]);
      setTotals({ totalDebit: 0, totalCredit: 0, netBalance: 0 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container report-container">
      <div className="header">
        <div className="tab active">Patient Report</div>
        <div className="tab">Monthly Report</div>
        <div className="tab">Annual Report</div>
      </div>

      <div className="content">
        <h1>Patient-wise Billing Report</h1>
        <p className="subtitle">View and filter detailed billing transactions for individual patients within a selected date range.</p>

        <div className="filter-section">
          <h2>Filter Patient Transactions</h2>
          <div className="filter-row">
            <input
              type="text"
              placeholder="Enter Patient ID"
              className="date-input"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
            />

            <input type="date" className="date-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <input type="date" className="date-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />

            <button className="apply-btn" onClick={fetchPayments} disabled={loading}>
              {loading ? "Loading..." : "Apply Filters"}
            </button>
          </div>

          {error && <p style={{ color: "#dc2626", marginTop: 12 }}>{error}</p>}
        </div>

        <div className="transaction-section">
          <h2>Detailed Transaction Log</h2>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Patient ID</th>
                <th>Invoice ID</th>
                <th>Debit Amount (INR)</th>
                <th>Credit Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: 20 }}>
                    {loading ? "Loading transactions..." : "No transactions found."}
                  </td>
                </tr>
              )}

              {payments.map((p) => {
                const debit = Number(p.CONSULTING_CHARGES || 0) + Number(p.PANCHKARMA_CHARGES || 0) + Number(p.MEDICINE_CHARGES || 0);
                const credit = Number(p.CURRENT_PAYMENT || 0);
                return (
                  <tr key={p.TRANSACTION_ID + p.LAST_UPDATE_DT}>
                    <td>{formatDate(p.LAST_UPDATE_DT)}</td>
                    <td>{p.PATIENT_ID}</td>
                    <td>{p.TRANSACTION_ID}</td>
                    <td className="debit">{formatCurrency(debit)}</td>
                    <td className="credit">{formatCurrency(credit)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="summary-section">
          <h2>Report Summary</h2>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-label">Total Debit (Invoices)</div>
              <div className="summary-value debit">{formatCurrency(totals.totalDebit)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Credit (Payments)</div>
              <div className="summary-value credit">{formatCurrency(totals.totalCredit)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Net Balance</div>
              <div className="summary-value balance">{formatCurrency(totals.totalDebit - totals.totalCredit)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="footer">© 2025 Clinic Billing Reports. All rights reserved.</div>

      <div className="watermark">Made with <strong>Visily</strong></div>
    </div>
  );
}
